import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

function TherapistListView({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [therapists, setTherapists] = useState([]);

  const loadTherapists = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getAssignedTherapists(true, token);
      setTherapists(data.therapists);
    } catch (err) {
      console.error('Error loading therapists:', err);
      setError(err.response?.data?.msg || 'Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTherapists();
  }, [token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/clinical-supervisor/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5">Assigned Therapists</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadTherapists}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active Clients</TableCell>
              <TableCell>Consultations</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {therapists.length > 0 ? (
              therapists.map((therapist) => (
                <TableRow
                  key={therapist._id}
                  hover
                  onClick={() => navigate(`/clinical-supervisor/therapists/${therapist._id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{therapist.name}</TableCell>
                  <TableCell>{therapist.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={therapist.status}
                      size="small"
                      color={therapist.status === 'Active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{therapist.stats?.activeClientsCount || 0}</TableCell>
                  <TableCell>{therapist.stats?.consultationCount || 0}</TableCell>
                  <TableCell>
                    {therapist.lastActivity ? (
                      <Tooltip title={new Date(therapist.lastActivity).toLocaleString()}>
                        <Typography variant="body2">
                          {formatDistanceToNow(new Date(therapist.lastActivity), { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          navigate(`/clinical-supervisor/therapists/${therapist._id}`);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No therapists assigned
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default TherapistListView;
